import {TDBinding, TDShape, TDSnapshot, TransformInfo, WhiteboardShape, WhiteboardStore} from '@modules/whiteboard'
import {TLBounds, Utils} from '@tldraw/core'
import Vec from '@tldraw/vec'

export class WhiteboardShapeUtilHelper {
  constructor(public store: WhiteboardStore) {}

  getTitle = (shape: WhiteboardShape) => {
    return this.store.shapeUtilFor(shape).getTitle(shape)
  }

  getRotatedBounds = (shape: WhiteboardShape) => {
    return this.store.shapeUtilFor(shape).getRotatedBounds(shape)
  }

  getCenter = (shape: WhiteboardShape) => {
    return this.store.shapeUtilFor(shape).getCenter(shape)
  }

  getBounds = (shape: WhiteboardShape) => {
    return this.store.shapeUtilFor(shape).getBounds(shape)
  }

  transformSingle = <T extends WhiteboardShape>(shape: T, bounds: TLBounds, info: TransformInfo<T>) => {
    return this.store.shapeUtilFor(shape).transformSingle(shape, bounds, info)
  }

  transform = <T extends WhiteboardShape>(shape: T, bounds: TLBounds, info: TransformInfo<T>) => {
    return this.store.shapeUtilFor(shape).transform(shape, bounds, info)
  }

  get shapeUtilFor() { return this.store.shapeUtilFor }

  onSessionComplete = <T extends TDShape>(shape: T) => {
    const delta = this.shapeUtilFor(shape).onSessionComplete?.(shape)
    if (!delta) return shape
    return {...shape, ...delta}
  }

  onChildrenChange = <T extends TDShape>(shape: T) => {
    if (!shape.children) return

    const {shapeUtilFor, store: {getShape}} = this

    const delta = shapeUtilFor(shape).onChildrenChange?.(
      shape,
      shape.children.map((id) => getShape(id))
    )

    if (!delta) return shape

    return {...shape, ...delta}
  }

  onBindingChange<T extends TDShape>(shape: T, binding: TDBinding, otherShape: TDShape) {
    const {shapeUtilFor, getBounds, getCenter} = this
    const delta = shapeUtilFor(shape).onBindingChange?.(
      shape,
      binding,
      otherShape,
      getBounds(otherShape),
      getCenter(otherShape)
    )
    if (!delta) return shape

    return {...shape, ...delta}
  }

  get getShape() { return this.store.getShape}

  updateBindings = (
    id: string,
    beforeShapes: Record<string, Partial<TDShape>> = {},
    afterShapes: Record<string, Partial<TDShape>> = {}
  ): TDSnapshot => {
    const {store: {bindings}, getShape, onBindingChange} = this
    bindings
      .filter((binding) => binding.fromId === id || binding.toId === id)
      .forEach(binding => {
        const beforeShape = getShape(binding.fromId),
          afterShape = getShape(binding.toId)

        if (beforeShape && afterShape) {
          if (!beforeShapes[binding.fromId]) {
            beforeShapes[binding.fromId] = beforeShape
          }

          if (!beforeShapes[binding.toId]) {
            beforeShapes[binding.toId] = afterShape
          }

          onBindingChange(
            beforeShape,
            binding,
            afterShape
          )

          afterShapes[binding.fromId] = beforeShape
          afterShapes[binding.toId] = afterShape
        }
      })

    return this.store.settings
  }

  /**
   * Rotate a shape around an origin point.
   * @param shape a shape.
   * @param center the shape's center in page space.
   * @param origin the page point to rotate around.
   * @param rotation the amount to rotate the shape.
   */
  getRotatedShapeMutation = <T extends TDShape>(
    shape: T, // in page space
    center: number[], // in page space
    origin: number[], // in page space (probably the center of common bounds)
    delta: number // The shape's rotation delta
  ): Partial<T> | void => {
    const {shapeUtilFor} = this
    // The shape's center relative to the shape's point
    const relativeCenter = Vec.sub(center, shape.point)

    // Rotate the center around the origin
    const rotatedCenter = Vec.rotWith(center, origin, delta)

    // Get the top left point relative to the rotated center
    const nextPoint = Vec.toFixed(Vec.sub(rotatedCenter, relativeCenter))

    // If the shape has handles, we need to rotate the handles instead
    // of rotating the shape. Shapes with handles should never be rotated,
    // because that makes a lot of other things incredible difficult.
    if (shape.handles !== undefined) {
      const change = this.shapeUtilFor(shape).onHandleChange?.(
        // Base the change on a shape with the next point
        {...shape, point: nextPoint},
        Object.fromEntries(
          Object.entries(shape.handles).map(([handleId, handle]) => {
            // Rotate each handle's point around the shape's center
            // (in relative shape space, as the handle's point will be).
            const point = Vec.toFixed(Vec.rotWith(handle.point, relativeCenter, delta))
            return [handleId, {...handle, point}]
          })
        ) as T['handles'],
        {shiftKey: false}
      )

      return change
    }

    // If the shape has no handles, move the shape to the new point
    // and set the rotation.

    // Clamp the next rotation between 0 and PI2
    const nextRotation = Utils.clampRadians((shape.rotation || 0) + delta)

    return {
      point: nextPoint,
      rotation: nextRotation
    } as Partial<T>
  }

  /*

  static transform<T extends TDShape>(shape: T, bounds: TLBounds, info: TLTransformInfo<T>) {
    const delta = TLDR.getShapeUtil(shape).transform(shape, bounds, info)
    if (!delta) return shape
    return {...shape, ...delta}
  }

   */
  //
  // getBounds = (shape: WhiteboardShape) => {
  //   return this.store.shapeUtilFor(shape).getBounds(shape)
  // }
  //
  // getBounds = (shape: WhiteboardShape) => {
  //   return this.store.shapeUtilFor(shape).getBounds(shape)
  // }
}


// WEBPACK FOOTER //
// ./src/modules/whiteboard/WhiteboardShapeUtilHelper.ts